// unmanaged
// /projects — the team dashboard (docs/project-tracking.md): every open
// customer analysis grouped by company, its stage, health and next action.
// Admin + InternalUser only; the backend router is the real gate.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link as MuiLink,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PageShell from "../PageShell";
import PageContainer from "../components/PageContainer";
import PageHeader from "../components/PageHeader";
import { tableHeaderCellSx } from "../components/DataCard";
import { useAuthStore } from "../store/authStore";
import { userRoles } from "../utils/roles";
import { fetchDashboard, fetchMeta } from "./projectApi";
import NewProjectDialog from "./NewProjectDialog";
import { CATCH_ALL_LABEL } from "./ProjectTagField";
import {
  ATTENTION_LABEL,
  HealthChip,
  RenewalChip,
  StageBar,
  StatusChip,
  daysLate,
  filterRows,
  formatDay,
  groupByCompany,
  personName,
} from "./projectParts";

function Kpi({ value, label }) {
  return (
    <Paper elevation={0} sx={{ p: 2, flex: "1 1 160px", borderRadius: 2, backgroundColor: "rgba(255,255,255,0.97)" }}>
      <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>{label}</Typography>
    </Paper>
  );
}

function Due({ row, today }) {
  if (!row.next_action_due) return <Typography variant="body2" sx={{ color: "text.disabled" }}>—</Typography>;
  const late = daysLate(row.next_action_due, today);
  return (
    <Typography variant="body2" sx={{ color: late > 0 ? "error.main" : "inherit", fontWeight: late > 0 ? 600 : 400, whiteSpace: "nowrap" }}>
      {formatDay(row.next_action_due)}{late > 0 ? ` · ${late} day${late === 1 ? "" : "s"} late` : ""}
    </Typography>
  );
}

function NextAction({ row }) {
  if (row.next_action) return <Typography variant="body2">{row.next_action}</Typography>;
  return <Typography variant="body2" sx={{ color: "error.main" }}>No next action</Typography>;
}

function AttentionChips({ row }) {
  // Health, late and missing next actions show in their own columns.
  const reasons = (row.attention || []).filter((r) => !["overdue", "no_next_action", "health"].includes(r));
  return reasons.map((r) => (
    <Chip key={r} size="small" color="warning" variant="outlined" label={ATTENTION_LABEL[r] || r} sx={{ mr: 0.5, mt: 0.5 }} />
  ));
}

// "3 input files · 5 runs", each opening that set on the Reports page.
export function ItemCounts({ project, counts }) {
  const c = counts || { inputs: 0, runs: 0 };
  const to = `/report-list?project=${encodeURIComponent(project)}`;
  const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
  return (
    <Typography variant="caption" component="div" sx={{ color: "text.secondary" }} onClick={(e) => e.stopPropagation()}>
      <MuiLink component={RouterLink} to={to}>{plural(c.inputs, "input file")}</MuiLink>
      {" · "}
      <MuiLink component={RouterLink} to={`${to}#runs`}>{plural(c.runs, "run")}</MuiLink>
    </Typography>
  );
}

function ProjectCard({ row, meta, today, onOpen }) {
  return (
    <Paper elevation={0} onClick={onOpen} sx={{ p: 2, cursor: "pointer", borderRadius: 2 }} data-testid={`project-${row.id}`}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>{row.id}</Typography>
          <Typography sx={{ fontWeight: 600 }}>{row.name}</Typography>
          <ItemCounts project={row.id} counts={row.counts} />
        </Box>
        <HealthChip health={row.health} />
      </Box>
      <Box sx={{ my: 1 }}><StageBar milestones={row.milestones} defs={meta?.milestones} stage={row.stage} /></Box>
      <NextAction row={row} />
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5, gap: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {personName(meta?.team, row.next_action_owner) || "Unassigned"}
        </Typography>
        <Due row={row} today={today} />
      </Box>
      <AttentionChips row={row} />
    </Paper>
  );
}

export default function Projects() {
  const user = useAuthStore((s) => s.user);
  const roles = userRoles(user);
  const isAllowed = roles.includes("Admin") || roles.includes("InternalUser");
  const navigate = useNavigate();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down("md"));
  // ?company=<id> (the Companies page links here) shows one company's projects.
  const [searchParams, setSearchParams] = useSearchParams();
  const companyFilter = searchParams.get("company");

  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [view, setView] = useState("all");
  const [showClosed, setShowClosed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const m = await fetchMeta();
      const res = await fetchDashboard(showClosed ? m.statuses : null, companyFilter);
      setMeta(m);
      setRows(res.records || []);
      setSummary(res.metadata || {});
    } catch (e) {
      setError(e?.message || "Could not load projects");
    } finally {
      setLoading(false);
    }
  }, [showClosed, companyFilter]);

  useEffect(() => {
    if (isAllowed) load();
  }, [isAllowed, load]);

  const visible = useMemo(() => filterRows(rows, view, user?.id), [rows, view, user?.id]);
  const groups = useMemo(() => groupByCompany(visible), [visible]);
  const today = summary.today;
  const open = (id) => navigate(`/projects/${encodeURIComponent(id)}`);

  if (!isAllowed) {
    return (
      <PageShell>
        <PageContainer>
          <Alert severity="error" sx={{ mt: 2 }}>Projects is limited to Admin and InternalUser roles.</Alert>
        </PageContainer>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageContainer>
        <PageHeader
          icon={<AccountTreeIcon />}
          title="Projects"
          subtitle="Every active customer analysis, where it stands, and what happens next."
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)} disabled={!meta}>
              New project
            </Button>
          }
        />

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <Kpi value={summary.open_projects ?? "—"} label={`Open projects across ${summary.open_companies ?? 0} customers`} />
          <Kpi value={summary.needs_attention ?? "—"} label="Need attention" />
          <Kpi value={summary.renewals ? summary.renewals.length : "—"} label="Renewals in the next 60 days" />
          <Kpi
            value={summary.median_turnaround_days != null ? `${summary.median_turnaround_days} days` : "—"}
            label="Median data-in to delivery, last 90 days"
          />
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={(_, v) => v && setView(v)}
            sx={{ backgroundColor: "rgba(255,255,255,0.97)" }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="attention">Needs attention</ToggleButton>
            <ToggleButton value="mine">Mine</ToggleButton>
          </ToggleButtonGroup>
          <FormControlLabel
            sx={{ color: "#fff" }}
            control={<Switch checked={showClosed} onChange={(_, v) => setShowClosed(v)} />}
            label="Show closed and cancelled"
          />
        </Box>

        {companyFilter && (
          <Alert severity="info" action={<Button size="small" onClick={() => setSearchParams({})}>Show all</Button>}>
            Showing {(meta?.companies || []).find((c) => c.id === companyFilter)?.name || companyFilter} only.
          </Alert>
        )}
        {view === "attention" && (summary.renewals || []).length > 0 && (
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 0.6, color: "#64748B", mb: 1 }}>
              Renewals
            </Typography>
            {summary.renewals.map((r) => (
              <Box key={r.company_id} sx={{ display: "flex", gap: 1, alignItems: "center", py: 0.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography>
                <RenewalChip days={r.days} />
              </Box>
            ))}
          </Paper>
        )}
        {summary.catch_all && (
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{CATCH_ALL_LABEL}</Typography>
            <ItemCounts project="none" counts={summary.catch_all} />
            <Typography variant="caption" sx={{ color: "text.secondary", flexBasis: "100%" }}>
              Staff work nobody put in a project. Open it to move each item to its project.
            </Typography>
          </Paper>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {loading && rows.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box>
        ) : visible.length === 0 ? (
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, color: "text.secondary" }}>
            {view === "attention" ? "Nothing needs attention." : view === "mine" ? "No projects name you." : "No projects yet."}
          </Paper>
        ) : narrow ? (
          groups.map((g) => (
            <Box key={g.company_id} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: 0.6 }}>
                {g.company_name}
              </Typography>
              {g.rows.map((r) => <ProjectCard key={r.id} row={r} meta={meta} today={today} onOpen={() => open(r.id)} />)}
            </Box>
          ))
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeaderCellSx}>Project</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Offering</TableCell>
                  <TableCell sx={{ ...tableHeaderCellSx, minWidth: 150 }}>Stage</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Health</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Next action</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Owner</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Due</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((g) => (
                  <React.Fragment key={g.company_id}>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ backgroundColor: "#F1F5F9", fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 0.6 }}>
                        {g.company_name}
                      </TableCell>
                    </TableRow>
                    {g.rows.map((r) => (
                      <TableRow key={r.id} hover onClick={() => open(r.id)} sx={{ cursor: "pointer" }} data-testid={`project-${r.id}`}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{r.id}</Typography>
                          <Typography variant="body2">{r.name}</Typography>
                          <ItemCounts project={r.id} counts={r.counts} />
                          {r.status !== "active" && <StatusChip status={r.status} />}
                          <AttentionChips row={r} />
                        </TableCell>
                        <TableCell>{r.offering || <Typography variant="body2" sx={{ color: "text.disabled" }}>Not set</Typography>}</TableCell>
                        <TableCell><StageBar milestones={r.milestones} defs={meta?.milestones} stage={r.stage} /></TableCell>
                        <TableCell><HealthChip health={r.health} /></TableCell>
                        <TableCell><NextAction row={r} /></TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{personName(meta?.team, r.next_action_owner) || "Unassigned"}</TableCell>
                        <TableCell><Due row={r} today={today} /></TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageContainer>
      <NewProjectDialog
        open={newOpen}
        meta={meta}
        onClose={() => setNewOpen(false)}
        onCreated={(p) => { setNewOpen(false); open(p.id); }}
      />
    </PageShell>
  );
}
